import React, { useContext, useState } from 'react';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import Logo from './Logo';

/** Primary submit / CTA — brand.primary base, brand.hover on hover. */
export const authSubmitButtonClass =
  'min-h-11 w-full rounded-2xl border-0 bg-brand-primary text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-[0.85] sm:min-h-[3.125rem]';

/** Text links on auth screens. */
export const authLinkClass =
  'text-sm font-medium text-brand-navy underline underline-offset-[0.2em] hover:text-brand-subtle sm:whitespace-nowrap';

/** Gradient brand column with logo + headline + tagline (signup, login, …). */
export function BrandAside({ titleKey, taglineKey, className = '' }) {
  const { translate } = useContext(LocalizeContext);
  return (
    <aside
      className={`relative isolate flex w-full shrink-0 flex-col justify-start gap-4 overflow-hidden bg-brand-aside p-4 text-white/90 sm:gap-6 sm:p-7 md:p-8 lg:min-h-screen lg:w-[min(42vw,28rem)] lg:shrink-0 lg:justify-center lg:gap-8 lg:px-10 lg:py-14 ${className}`.trim()}
    >
      <div className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(to_bottom,rgba(59,130,246,0.045)_0%,transparent_30%,transparent_70%,rgba(12,42,94,0.38)_100%)]" aria-hidden />
      <div className="relative z-10 flex flex-col items-center space-y-3 text-center sm:items-start sm:space-y-5 sm:text-left">
        <Logo />
        <h2 className="font-serif text-xl font-medium leading-snug text-white sm:text-2xl md:text-3xl">
          {translate(titleKey)}
        </h2>
        <p className="mx-auto max-w-xs text-sm italic leading-relaxed text-white/80 sm:mx-0 sm:max-w-none sm:text-base">
          {translate(taglineKey)}
        </p>
        <span
          className="mx-auto mt-2 block h-1 w-12 shrink-0 rounded-full bg-brand-primary sm:mx-0 sm:mt-3 sm:w-14"
          aria-hidden
        />
      </div>
    </aside>
  );
}

/** Labeled text input with optional error line. Password fields get a show/hide control by default. */
export function LabeledInputField({
  id,
  label,
  error,
  onValueChange,
  className = '',
  hidePasswordToggle = false,
  ...props
}) {
  const { translate } = useContext(LocalizeContext);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = props.type === 'password' && !hidePasswordToggle;
  const inputType = isPassword ? (passwordVisible ? 'text' : 'password') : props.type;

  const baseInput =
    'min-h-11 w-full rounded-3xl border bg-surface py-2.5 text-sm shadow-[inset_0_1px_2px_rgba(12,27,51,0.04)] focus:outline-none sm:min-h-12 sm:py-3 border-brand-navy/14 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30';
  const errorInput =
    'min-h-11 w-full rounded-3xl border bg-surface py-2.5 text-sm shadow-[inset_0_1px_2px_rgba(12,27,51,0.04)] focus:outline-none sm:min-h-12 sm:py-3 border-brand-error focus:border-brand-error focus:ring-2 focus:ring-brand-error/25';

  const paddingX = isPassword ? ' pl-4 pr-11 sm:pl-[1.125rem] sm:pr-[2.875rem]' : ' px-4 sm:px-[1.125rem]';

  return (
    <div className={className}>
      <label
        className="mb-1 block font-sans text-xs font-semibold uppercase tracking-[0.2em] text-brand-navy/55 sm:mb-1.5 md:mb-2"
        htmlFor={id}
      >
        {label}
      </label>
      <div className="relative">
        <input
          {...props}
          id={id}
          type={inputType}
          className={`${error ? errorInput : baseInput}${paddingX}`}
          onChange={e => {
            props.onChange?.(e);
            onValueChange?.(e.target.value);
          }}
          aria-invalid={!!error}
        />
        {isPassword && (
          <button
            type="button"
            disabled={props.disabled}
            onClick={() => setPasswordVisible(v => !v)}
            onMouseDown={e => e.preventDefault()}
            aria-label={passwordVisible ? translate('hide_password') : translate('show_password')}
            aria-pressed={passwordVisible}
            aria-controls={id}
            className="absolute bg-transparent right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-brand-navy/45 hover:text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/35 disabled:pointer-events-none disabled:opacity-50 sm:right-2.5 sm:h-10 sm:w-10"
          >
            {passwordVisible ? (
              <EyeSlash className="h-[18px] w-[18px]" aria-hidden />
            ) : (
              <Eye className="h-[18px] w-[18px]" aria-hidden />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="m-0 text-xs leading-snug text-brand-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
